import { callTool, type McpClientHandle } from './mcp-client.js';

/**
 * MCP tool result envelope: `{ content: [{ type: 'text', text: '<json>' }, ...] }`.
 * Each tool returns its payload as a JSON-stringified text block, so we
 * parse the first text block back to whatever shape the tool produced.
 */
function parseFirstJsonBlock<T>(result: unknown): T {
  const r = result as
    | { content?: { type?: string; text?: string }[]; isError?: boolean }
    | undefined;
  const block = r?.content?.find((b) => b.type === 'text');
  if (!block?.text) throw new Error('mcp_tool_empty_result');
  if (r?.isError) throw new Error(block.text);
  return JSON.parse(block.text) as T;
}

export interface QueryHit {
  readonly nodeId: string;
  readonly score: number;
  readonly preview?: string;
  readonly filePath?: string;
}

export interface QueryError {
  readonly slug: string;
  readonly error: string;
}

export interface QueryResult {
  readonly hits: readonly QueryHit[];
  readonly errors: readonly QueryError[];
}

export async function querySymbols(
  handle: McpClientHandle,
  args: { repo: string; text: string; topK?: number },
): Promise<QueryResult> {
  const result = await callTool(handle, 'query', {
    repo: args.repo,
    query: args.text,
    topK: args.topK,
  });
  const parsed = parseFirstJsonBlock<{ hits?: QueryHit[]; errors?: QueryError[] } | QueryHit[]>(
    result,
  );
  if (Array.isArray(parsed)) return { hits: parsed, errors: [] };
  return { hits: parsed.hits ?? [], errors: parsed.errors ?? [] };
}

export interface ContextResult {
  readonly nodeId: string;
  readonly callers?: readonly { id: string; name?: string }[];
  readonly callees?: readonly { id: string; name?: string }[];
  readonly processes?: readonly { id: string; name?: string }[];
  readonly subgraph?: {
    readonly nodes: readonly { id: string; label?: string; kind?: string }[];
    readonly edges: readonly { source: string; target: string; type?: string }[];
  };
}

export async function getContext(
  handle: McpClientHandle,
  args: { repo: string; symbol: string; depth?: number },
): Promise<ContextResult> {
  const result = await callTool(handle, 'context', {
    repo: args.repo,
    symbolId: args.symbol,
    depth: args.depth,
  });
  return parseFirstJsonBlock<ContextResult>(result);
}

export interface ImpactEdge {
  readonly fromId: string;
  readonly toId: string;
  readonly type: string;
  readonly confidence: number;
  readonly hop: number;
}

export interface ImpactResult {
  readonly upstream: readonly ImpactEdge[];
  readonly downstream: readonly ImpactEdge[];
}

export async function getImpact(
  handle: McpClientHandle,
  args: { repo: string; target: string; depth?: number },
): Promise<ImpactResult> {
  const result = await callTool(handle, 'impact', {
    repo: args.repo,
    symbolId: args.target,
    maxDepth: args.depth,
  });
  const parsed = parseFirstJsonBlock<ImpactResult | { edges?: ImpactEdge[] }>(result);
  if ('upstream' in parsed) return parsed;
  const edges = parsed.edges ?? [];
  return {
    upstream: edges.filter((e) => e.toId.length > 0),
    downstream: edges.filter((e) => e.fromId.length > 0),
  };
}

export type CypherRow = Readonly<Record<string, unknown>>;

export interface CypherResult {
  readonly columns: readonly string[];
  readonly rows: readonly CypherRow[];
}

export async function runCypher(
  handle: McpClientHandle,
  args: { repo: string; cypher: string },
): Promise<CypherResult> {
  const result = await callTool(handle, 'cypher', args);
  const parsed = parseFirstJsonBlock<CypherResult | { error: string }>(result);
  if ('error' in parsed) throw new Error(parsed.error);
  return parsed;
}
